/**
 * FILE-OVERVIEW:
 * - 役割: NewsML2CTS XMLを生成する業務ロジックの中核。
 * - 主な連携: components/Calendar/EventDialog.js, components/Memo/index.js
 * - 本番運用上の注意: 項目名や日時フォーマット変更は外部連携仕様に必ず合わせる。
 */

// NewsML2CTS 形式のメモXMLを生成するユーティリティ
// 参考テンプレート: src/template/NewsML2CTS.xml

/**
 * 出稿XMLビルダーの入力。
 *
 * 5つのビルダーはテスト(newsmlBuilder.test.js)で同一のパラメータを渡して
 * 横断検証しているため、型も共通の1つにしている。
 * 各項目を実際に読むビルダーは末尾のコメントを参照。
 * 値・書式はCTS側の取り込み仕様に直結するため、型付けのために既定値を
 * 変えてはならない。
 */
export interface NewsML2CTSParams {
  /** 記事・写真ビルダーのCatalog出力。スケジュールからのメモ出稿ではfalse */
  includeCatalog?: boolean;
  /** テーマ名（メモXMLの HeadLine / dhListNo） */
  themeName?: string;
  /** テーマの内容（メモXMLのみ） */
  themeContent?: string;
  /** 種別。dhNewsGenre に出る。実体XMLの既定は全角の 'ＮＥＣ' */
  memoType?: string;
  /** 重要度。urgencyMap で FormalName の数字へ変換する */
  memoPriority?: string;
  /** 登録予定日 YYYY-MM-DD。NyuukouYotei の元になる */
  memoRegistrationDate?: string;
  /** 分類。Subject の FormalName にそのまま出る（空なら空のまま） */
  memoCategory?: string;
  /** 記者名（ByLine） */
  reporterName?: string;
  /** スケジュール開始日。NyuukouYotei と DateId のフォールバック */
  eventStart?: Date | string | null;
  /** ログインID（CreatorID） */
  username?: string;
  /** スケジュールID。ID採番の基点 */
  scheduleId?: string | number | null;
  /** 素材種別（記事 / 写真 / ＥＰＳ / ＰＤＦ …）。ID内のスラッグになる */
  materialKind?: string;
  /** 素材連番。ID末尾の4桁 */
  materialSerial?: number | string;
  /** LinkURL プロパティ */
  linkUrl?: string;
  /** EditInfo の NewsLineText */
  editInfo?: string;
  /** 素材見出し */
  materialHeadline?: string;
  /** 行数指定。articleBody が空のときの本文フォールバック */
  materialLines?: string;
  /** 記事本文 */
  articleBody?: string;
  /** 写真のキャプション */
  caption?: string;
  /** 実体ファイル名。拡張子から Format / MimeType を決める */
  entityFileName?: string;
  /** 実体画像の幅（px）。高さと揃っているときだけ Characteristics に出る */
  imageWidth?: number | string;
  /** 実体画像の高さ（px） */
  imageHeight?: number | string;
  /** 既存の themeDuid。更新時にIDを維持するため必ず引き継ぐ */
  existingThemeDuid?: string;
  /** 既存の newsItemId。同上 */
  existingNewsItemId?: string;
  /** 実体XMLが参照する親メモのID */
  memoId?: string;
  /** 実体XMLが参照する子メモのID */
  childMemoId?: string;
}

/** 5つのビルダーに共通のシグネチャ。テストで一覧にまとめて回すために使う */
export type NewsML2CTSBuilder = (params?: NewsML2CTSParams) => string;

/** 重要度から Priority/Urgency の FormalName を引く表 */
type UrgencyMap = Record<string, string>;

/** themeDuid / newsItemId の採番に使う項目 */
interface BuildIdsParams {
  scheduleId?: string | number | null;
  materialKind?: string;
  materialSerial?: number | string;
  /** 呼び出し側が渡しているが現在の採番には使っていない（過去の採番方式の名残） */
  baseSeq?: string;
  /** 同上 */
  scheduleTimeStr?: string;
  existingThemeDuid?: string;
  existingNewsItemId?: string;
}

/** NyuukouYotei（YYYYMMDDThhmmss+0900）の組み立てに使う項目 */
interface BuildNyuukouYoteiParams {
  memoRegistrationDate?: string;
  eventStart?: Date | string | null;
  /** 登録予定日もスケジュール開始日も無いときの基準日 */
  fallbackDate: Date;
  /** タイムゾーン表記。'+0900' 固定 */
  tz: string;
}

/** ゼロ埋め。日時の各パーツを組み立てるのに使う */
const pad = (n: number | string, len = 2): string => String(n).padStart(len, '0');

/** YYYYMMDD */
const ymd = (d: Date): string => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;

/** YYYY/MM/DD（DateLine用） */
const ymdSlash = (d: Date): string => `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;

/** hhmmss */
const hhmmss = (d: Date): string => `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;

/** hh:mm:ss */
const hhmmssColon = (d: Date): string => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

/** タイムゾーン表記。CTS側の仕様で +0900 固定 */
const TZ = '+0900';

/** XMLの特殊文字をエスケープする。null / undefined は空文字にする */
const escapeXml = (s: unknown): string => s == null ? '' : String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

/** 重要度 → Priority / Urgency の FormalName */
const URGENCY_MAP: UrgencyMap = {
  'ベタ': '5',
  'トップ': '1',
  '段もの': '3',
  '取り置き可': '6',
  'その他': '7',
  '決まりもの': '8',
};

// 素材種別を英字スラッグへ正規化（ファイル名/ID生成と同じルール）
const materialKindToSlug = (kind: unknown): string => {
  const raw = kind == null ? '' : String(kind).trim();
  const lower = raw.toLowerCase();
  if (raw === '記事') return 'article';
  if (raw === '写真' || raw === '画像') return 'photo';
  if (raw === 'ＥＰＳ' || lower === 'eps') return 'eps';
  if (raw === 'ＰＤＦ' || lower === 'pdf') return 'pdf';
  if (raw === 'エクセル' || lower === 'excel') return 'excel';
  if (raw === '動画' || lower === 'video') return 'video';
  return lower || 'article';
};

export const generateUniqueNoIdToken = (): string => {
  const timePart = Date.now().toString(36);
  const randomPart = Math.random().toString(36).slice(2, 10);
  const cryptoPart = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 8)
    : '';
  return `${timePart}${randomPart}${cryptoPart}`;
};

export const toMemoIdSegment = (value: unknown): string => {
  // split('-') は必ず1要素以上返すため pop() は実質 string。strict のために ?? '' で補う
  const raw = value == null ? '' : (String(value).split('-').pop() ?? '');
  const normalized = raw.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const fallback = generateUniqueNoIdToken();
  return `${normalized}${fallback}`.slice(0, 12);
};

// スケジュールIDベースで themeDuid / newsItemId を生成（指定が無ければ従来の乱数ベース）
const buildIds = ({
  scheduleId,
  materialKind,
  materialSerial,
  existingThemeDuid,
  existingNewsItemId,
}: BuildIdsParams): { themeDuid: string; newsItemId: string } => {
  // 既存IDが指定されている場合はそのまま使用（メモ編集時）
  if (existingThemeDuid && existingNewsItemId) {
    return {
      themeDuid: existingThemeDuid,
      newsItemId: existingNewsItemId,
    };
  }

  const slug = materialKindToSlug(materialKind);
  const serial = String(materialSerial || 1).padStart(4, '0');

  if (scheduleId && String(scheduleId).toLowerCase() !== 'noid') {
    const shortScheduleId = toMemoIdSegment(scheduleId);
    return {
      themeDuid: `NOTET-${shortScheduleId}`,
      newsItemId: `NOTEM-${shortScheduleId}-${slug}-${serial}`,
    };
  }

  const uniqueToken = toMemoIdSegment(generateUniqueNoIdToken());

  return {
    themeDuid: `NOTET-${uniqueToken}`,
    newsItemId: `NOTEM-${uniqueToken}-${slug}-${serial}`,
  };
};

// NyuukouYotei は登録予定日優先。未指定時はスケジュール日付の 00:00:00 を使う
const buildNyuukouYotei = ({ memoRegistrationDate, eventStart, fallbackDate, tz }: BuildNyuukouYoteiParams): string => {
  const isValidDate = (d: unknown): d is Date => d instanceof Date && !Number.isNaN(d.getTime());

  if (memoRegistrationDate) {
    return `${String(memoRegistrationDate).replace(/-/g, '')}T000000${TZ}`;
  }

  const start = eventStart ? new Date(eventStart) : null;
  if (isValidDate(start)) {
    const y = start.getFullYear();
    const m = pad(start.getMonth() + 1);
    const d = pad(start.getDate());
    return `${y}${m}${d}T000000${TZ}`;
  }

  const baseDate = `${fallbackDate.getFullYear()}-${pad(fallbackDate.getMonth() + 1)}-${pad(fallbackDate.getDate())}`;
  return `${String(baseDate).replace(/-/g, '')}T000000${TZ}`;
};

// dhNewsGenre は「テーマの種別」を使用し、空の場合も空のままにする
const resolveDhNewsGenre = (memoType: unknown): string => {
  const value = memoType == null ? '' : String(memoType).replace(/\u3000/g, ' ').trim();
  return value;
};

/**
 * @param {Object} params
 * @param {string} params.themeName テーマ名
 * @param {string} params.themeContent テーマの内容
 * @param {string} params.memoType 種別 (例: NEC)
 * @param {string} params.memoPriority 重要度 (例: ベタ/トップ 等)
 * @param {string} params.memoRegistrationDate 登録予定日 YYYY-MM-DD
 * @param {string} params.memoCategory 分類 (例: 社会)
 * @param {string} params.reporterName 記者名（ByLine）
 * @param {Date|string} [params.eventStart] スケジュール開始日
 * @param {string} [params.username] ログインID（CreatorID）
 * @returns {string} NewsML2CTS 準拠のXML文字列（UTF-16BE宣言）
 */
export function buildMemoNewsML2CTS(params?: NewsML2CTSParams): string {
  const {
    themeName = '',
    themeContent = '',
    materialHeadline,
    materialLines = '',
    memoType = 'NEC',
    memoPriority = 'ベタ',
    memoRegistrationDate = '',
    memoCategory = '',
    reporterName = '未設定',
    eventStart,
    username = 'user',
    scheduleId,
    materialKind = '記事',
    materialSerial = 1,
    existingThemeDuid,
    existingNewsItemId,
    linkUrl = '',
    editInfo = '',
  } = params || {};

  const now = new Date();

  const startDate = eventStart ? new Date(eventStart) : now;
  const dateId = ymd(startDate);
  const dateLine = `${ymdSlash(startDate)}`; // 例: 2025/10/07
  const dateAndTime = `${ymd(now)}T${hhmmss(now)}${TZ}`; // 例: 20251007T102436+0900

  // NewsItemId / dhDuid をスケジュールIDベースで生成（指定が無ければ従来の乱数ベース）
  const baseSeq = `${ymd(now)}${hhmmss(now)}${String(Math.floor(Math.random() * 90) + 10)}`;
  const scheduleTimeStr = eventStart
    ? `${ymd(startDate)}${hhmmss(startDate)}`
    : (memoRegistrationDate
        ? `${String(memoRegistrationDate).replace(/-/g, '')}000000`
        : `${ymd(now)}${hhmmss(now)}`);
  // メモ画面の編集では既存のMemoID/ChildMemoIDを引き継ぐ（変わるとCTS側で別素材になる）
  const { newsItemId, themeDuid } = buildIds({
    scheduleId,
    materialKind,
    materialSerial,
    baseSeq,
    scheduleTimeStr,
    existingThemeDuid,
    existingNewsItemId,
  });


  const nyuukouYotei = buildNyuukouYotei({
    memoRegistrationDate,
    eventStart,
    fallbackDate: startDate,
    tz: TZ,
  });

  // 重要度（数字）: 例としてベタ=1/トップ=5 等、暫定マッピング
  /*
  const urgencyMap: UrgencyMap = {
    'ベタ': '1',
    'トップ': '5',
    '段もの': '3',
    '取り置き可': '2',
    'その他': '2',
    '決まりもの': '1',
  };
  const urgencyFormalName = URGENCY_MAP[memoPriority] || '1';
  */
  const urgencyFormalName = URGENCY_MAP[memoPriority] || '5';

  // 記事メモの予定行数。写真系はテンプレートどおり空要素のみ。
  const dhItemLineText = materialKindToSlug(materialKind) === 'article' ? String(materialLines ?? '') : '';

  // dhNewsGenre はテーマの種別を使用し、未入力時も空のまま出力
  const necGenre = resolveDhNewsGenre(memoType);

  return `<?xml version="1.0" encoding="UTF-16BE"?>
<NewsML>
  <NewsEnvelope>
    <DateAndTime>${escapeXml(dateAndTime)}</DateAndTime>
    <NewsProduct FormalName="NEC-NewsML:1.0" Scheme="NecNewsProduct"/>
    <Priority FormalName="${escapeXml(urgencyFormalName)}" Scheme="IptcPriority"/>
  </NewsEnvelope>
  <NewsItem>
    <Identification>
      <NewsIdentifier>
        <ProviderId>daily-tohoku.co.jp</ProviderId>
        <DateId>${escapeXml(dateId)}</DateId>
        <NewsItemId>${escapeXml(newsItemId)}</NewsItemId>
        <RevisionId PreviousRevision="0" Update="N">1</RevisionId>
        <PublicIdentifier>urn:newsml:daily-tohoku.co.jp:${escapeXml(dateId)}:${escapeXml(newsItemId)}:1</PublicIdentifier>
      </NewsIdentifier>
    </Identification>
    <NewsManagement>
      <NewsItemType FormalName="News"/>
      <FirstCreated>${escapeXml(dateAndTime)}</FirstCreated>
      <ThisRevisionCreated>${escapeXml(dateAndTime)}</ThisRevisionCreated>
      <Status FormalName="Usable" Scheme="IptcStatus"/>
      <Urgency FormalName="${escapeXml(urgencyFormalName)}" Scheme=""/>
    </NewsManagement>
    <NewsComponent Duid="NC001-root" xml:lang="ja-JP" EquivalentsList="no">
      <BasisForChoice>./Role/@FormalName</BasisForChoice>
      <NewsLines>
        <HeadLine></HeadLine>
        <ByLine>${escapeXml(reporterName)}</ByLine>
        <DateLine>${escapeXml(dateLine)}</DateLine>
        <CopyrightLine>Copyright(C) ${escapeXml(String(now.getFullYear()))} (c)DAILYTOHOKU All rights reserved.</CopyrightLine>
        <NewsLine>
          <NewsLineType FormalName="EditInfo" Scheme="NskNewsLineType"/>
          <NewsLineText>${escapeXml(editInfo || '')}</NewsLineText>
        </NewsLine>
      </NewsLines>
      <AdministrativeMetadata>
        <Provider>
          <Party FormalName="DAILYTOHOKU"/>
        </Provider>
        <Creator>
          <Party FormalName="DAILYTOHOKU"/>
        </Creator>
      </AdministrativeMetadata>
      <RightsMetadata>
        <Copyright>
          <CopyrightHolder>THE DAILY TOHOKU SHIMBUN</CopyrightHolder>
          <CopyrightDate>${escapeXml(String(now.getFullYear()))}</CopyrightDate>
        </Copyright>
      </RightsMetadata>
      <DescriptiveMetadata>
        <Language FormalName="ja" Scheme="ISO639"/>
        <SubjectCode>
          <Subject FormalName="${escapeXml(memoCategory || '')}"/>
        </SubjectCode>
      </DescriptiveMetadata>
      <Metadata>
        <MetadataType FormalName="NecProperties" Scheme="NecMetadataTypes" />
        <Property FormalName="IsDesk" Value="true" Scheme="NecProperties"/>
        <Property FormalName="FromHotFolder" Value="on" Scheme="NecProperties"/>
        <Property FormalName="LinkURL" Value="${escapeXml(linkUrl || '')}" Scheme="NecProperties"/>
        <Property FormalName="CreatorID" Value="${escapeXml(username)}" Scheme="NecProperties"/>
        <Property FormalName="MaterialKind" Value="${escapeXml(materialKind)}"/>
        <Property FormalName="NyuukouYotei" Value="${escapeXml(nyuukouYotei)}"/>
      </Metadata>
      <ContentItem>
        <MediaType FormalName="Text"/>
        <Format FormalName="KdDHKmMLv1.0"/>
        <MimeType FormalName="text/xml"/>
        <DataContent>
          <InContent version="1.0">
            <InMetadata>
              <InAdminInfo>
                <InProductId></InProductId>
                <InDateTime></InDateTime>
                <InFormat>
                  <InFormatType>フルタグ書式</InFormatType>
                  <InFormatComment>棒</InFormatComment>
                </InFormat>
                <InModifyInfo>新規</InModifyInfo>
              </InAdminInfo>
              <InCategoryInfo>
                <InNewsCategory>
                  <InNewsGenre>メモ</InNewsGenre>
                </InNewsCategory>
                <InNewsLine>
                  <InHeadLine>${escapeXml(materialHeadline ?? themeName)}</InHeadLine>
                </InNewsLine>
              </InCategoryInfo>
              <InNewsInfo>
                <InNewsKindInfo>
                  <InNewsKind>メモ</InNewsKind>
                </InNewsKindInfo>
              </InNewsInfo>
            </InMetadata>
            <InData>
              <KdDHKmML>
                <dhitem dhitem-type="text">
                  <dhRegisterName></dhRegisterName>
                  <dhol>
                    <dhli dhDuid="${escapeXml(themeDuid)}" dhli-type="main">
                      <dhListNo>${escapeXml(themeName || '')}</dhListNo>
                      <dhp/>
                      <dhp>${escapeXml(themeContent || '')}</dhp>
                      <dhMemoCaution>
                        <dhp></dhp>
                      </dhMemoCaution>
                      <dhol>
                        <dhli dhDuid="${escapeXml(newsItemId)}" dhli-type="spare" dhrole="本記">
                          <dhRelateInformation>
                            <dhTimeTable></dhTimeTable>
                            <dhNewsObject>
                              <dhNewsCategory>
                                <dhNewsGenre>${escapeXml(necGenre)}</dhNewsGenre>
                                <dhNewsNo></dhNewsNo>
                              </dhNewsCategory>
                            </dhNewsObject>
${materialKindToSlug(materialKind) === 'article' ? `                            <dhItemLine>${escapeXml(dhItemLineText)}</dhItemLine>\n` : ''}                            <dhItemLine/>
                          </dhRelateInformation>
                        </dhli>
                      </dhol>
                    </dhli>
                  </dhol>
                </dhitem>
              </KdDHKmML>
            </InData>
          </InContent>
        </DataContent>
      </ContentItem>
    </NewsComponent>
  </NewsItem>
</NewsML>`;
}

/**
 * 記事素材用 NewsML2CTS
 * materialHeadline を InHeadLine と dhRelateInformation の見出し系に反映
 *
 * 主な仕様:
 * - NyuukouYotei は memoRegistrationDate 優先で YYYYMMDDT000000+0900 を生成
 * - newsItemId/themeDuid は scheduleId と素材連番から決定
 * - 出力は UTF-16BE 宣言付き XML 文字列
 *
 * @param {Object} params 記事素材用の出稿パラメータ
 * @returns {string} NewsML2CTS XML
 */
export function buildArticleNewsML2CTS(params?: NewsML2CTSParams): string {
  const {
    themeName = '',
    memoPriority = 'ベタ',
    memoRegistrationDate = '',
    memoCategory = '',
    reporterName = '未設定',
    eventStart,
    username = 'user',
    materialHeadline = '',
    materialLines = '',
    articleBody = '',
    scheduleId,
    materialKind = '記事',
    materialSerial = 1,
    memoType = 'NEC',
    existingThemeDuid,
    existingNewsItemId,
    linkUrl = '',
    editInfo = '',
  } = params || {};

  const now = new Date();

  const startDate = eventStart ? new Date(eventStart) : now;
  const dateId = ymd(startDate);
  const dateAndTime = `${ymd(now)}T${hhmmss(now)}${TZ}`;
  const baseSeq = `${ymd(now)}${hhmmss(now)}${String(Math.floor(Math.random() * 90) + 10)}`;
  // themeDuid をスケジュールIDベースで生成（指定が無ければスケジュール日時ベース）
  const scheduleTimeStr = eventStart
    ? `${ymd(startDate)}${hhmmss(startDate)}`
    : (memoRegistrationDate
        ? `${String(memoRegistrationDate).replace(/-/g, '')}000000`
        : `${ymd(now)}${hhmmss(now)}`);
  const { newsItemId, themeDuid } = buildIds({
    scheduleId,
    materialKind,
    materialSerial,
    baseSeq,
    scheduleTimeStr,
    existingThemeDuid,
    existingNewsItemId,
  });

  const nyuukouYotei = buildNyuukouYotei({
    memoRegistrationDate,
    eventStart,
    fallbackDate: startDate,
    tz: TZ,
  });
  const urgencyFormalName = URGENCY_MAP[memoPriority] || '5';
  const necGenre = resolveDhNewsGenre(memoType);
  const articleBodyText = articleBody != null && String(articleBody).trim() !== ''
    ? String(articleBody)
    : (materialLines == null ? '' : String(materialLines));

  return `<?xml version="1.0" encoding="UTF-16BE"?>
<NewsML>
${params?.includeCatalog === false ? '' : '  <Catalog Href="../catalog/catalog.NecMasterCatalog.xml"/>\n'}  <NewsEnvelope>
    <DateAndTime>${escapeXml(dateAndTime)}</DateAndTime>
    <NewsProduct FormalName="NEC-NewsML:1.0" Scheme="NecNewsProduct"/>
    <Priority FormalName="${escapeXml(urgencyFormalName)}" Scheme="IptcPriority"/>
  </NewsEnvelope>
  <NewsItem>
    <Identification>
      <NewsIdentifier>
        <ProviderId>daily-tohoku.co.jp</ProviderId>
        <DateId>${escapeXml(dateId)}</DateId>
        <NewsItemId>${escapeXml(newsItemId)}</NewsItemId>
        <RevisionId PreviousRevision="1" Update="N">2</RevisionId>
        <PublicIdentifier>urn:newsml:daily-tohoku.co.jp:${escapeXml(dateId)}:${escapeXml(newsItemId)}:2</PublicIdentifier>
      </NewsIdentifier>
    </Identification>
    <NewsManagement>
      <NewsItemType FormalName="News"/>
      <FirstCreated>${escapeXml(dateAndTime)}</FirstCreated>
      <ThisRevisionCreated>${escapeXml(dateAndTime)}</ThisRevisionCreated>
      <Status FormalName="Usable" Scheme="IptcStatus"/>
      <Urgency FormalName="${escapeXml(urgencyFormalName)}" Scheme=""/>
    </NewsManagement>
    <NewsComponent Duid="NC001-root" xml:lang="ja-JP" EquivalentsList="no">
      <BasisForChoice>./Role/@FormalName</BasisForChoice>
      <NewsLines>
        <HeadLine>${escapeXml(materialHeadline || themeName || '')}</HeadLine>
        <ByLine>${escapeXml(reporterName)}</ByLine>
        <CopyrightLine>Copyright(C) ${escapeXml(String(now.getFullYear()))} (c)DAILYTOHOKU All rights reserved.</CopyrightLine>
        <NewsLine>
          <NewsLineType FormalName="EditInfo" Scheme="NskNewsLineType"/>
          <NewsLineText>${escapeXml(editInfo || '')}</NewsLineText>
        </NewsLine>
      </NewsLines>
      <AdministrativeMetadata>
        <Provider>
          <Party FormalName="DAILYTOHOKU" Scheme="NskParty"/>
        </Provider>
        <Creator>
          <Party FormalName="DAILYTOHOKU" Scheme="NskParty"/>
        </Creator>
      </AdministrativeMetadata>
      <RightsMetadata>
        <Copyright>
          <CopyrightHolder>THE DAILY TOHOKU SHIMBUN</CopyrightHolder>
          <CopyrightDate>${escapeXml(String(now.getFullYear()))}</CopyrightDate>
        </Copyright>
      </RightsMetadata>
      <DescriptiveMetadata>
        <Language FormalName="ja" Scheme="ISO639"/>
        <SubjectCode>
          <Subject FormalName="${escapeXml(memoCategory || '')}"/>
        </SubjectCode>
      </DescriptiveMetadata>
      <Metadata>
        <MetadataType FormalName="NecProperties" Scheme="NecMetadataTypes"/>
        <Property FormalName="IsDesk" Value="false" Scheme="NecProperties"/>
        <Property FormalName="FromHotFolder" Value="on" Scheme="NecProperties"/>
        <Property FormalName="NewsGenre" Value="${escapeXml(necGenre)}" Scheme="NecProperties"/>
        <Property FormalName="MemoID" Value="${escapeXml(themeDuid || '')}" Scheme="NecProperties"/>
        <Property FormalName="ChildMemoID" Value="${escapeXml(newsItemId)}" Scheme="NecProperties"/>
        <Property FormalName="SecondUse" Value="true" Scheme="NecProperties"/>
        <Property FormalName="RePublishingFlag" Value="true" Scheme="NecProperties"/>
        <Property FormalName="HumanRights" Value="off" Scheme="NecProperties"/>
        <Property FormalName="LinkURL" Value="${escapeXml(linkUrl || '')}" Scheme="NecProperties"/>
        <Property FormalName="QuickReportNgFlag" Value="off" Scheme="NecProperties"/>
        <Property FormalName="CreatorID" Value="${escapeXml(username)}" Scheme="NecProperties"/>
        <Property FormalName="MaterialKind" Value="${escapeXml(materialKind)}" Scheme="NecProperties"/>
        <Property FormalName="NyuukouYotei" Value="${escapeXml(nyuukouYotei)}" Scheme="NecProperties"/>
      </Metadata>
      <Metadata>
        <MetadataType FormalName="KyodoMetadata" Scheme="KydMetadataTypes"/>
        <Property FormalName="RelatedInfo" Scheme="KydProperty">
          <Property FormalName="RelatedTo" Scheme="KydProperty">
            <Property FormalName="RelatedID" Scheme="KydProperty" Value="${escapeXml(themeDuid)}"/>
            <Property FormalName="RelatedKind" Scheme="KydProperty" Value="memo"/>
          </Property>
          <Property FormalName="RelatedTo" Scheme="KydProperty">
            <Property FormalName="RelatedID" Scheme="KydProperty" Value="${escapeXml(newsItemId)}"/>
            <Property FormalName="RelatedKind" Scheme="KydProperty" Value="child_memo"/>
          </Property>
        </Property>
      </Metadata>
      <NewsComponent Duid="NC001-Main" xml:lang="ja-JP">
        <Role FormalName="Main" Scheme="IptcRole"/>
        <ContentItem>
          <MediaType FormalName="Text" Scheme="IptcMediaTypes"/>
          <MimeType FormalName="text/plain" Scheme="IptcMimeTypes"/>
          <Characteristics>
            <Property FormalName="FlowDirection" Value="0" Scheme="NecProperties"/>
            <Property FormalName="CharsPerLine" Value="12" Scheme="NecProperties"/>
            <Property FormalName="MidashiWaku" Value="0,0,0" Scheme="NecProperties"/>
          </Characteristics>
          <DataContent>${escapeXml(articleBodyText || '')}</DataContent>
        </ContentItem>
      </NewsComponent>
    </NewsComponent>
  </NewsItem>
</NewsML>`;
}

/**
 * 写真/EPS/PDF 用 NewsML2CTS（テンプレは写真ベース）
 * materialKind を NecProperties.MaterialKind に反映
 *
 * 主な仕様:
 * - NyuukouYotei は memoRegistrationDate 優先で YYYYMMDDT000000+0900 を生成
 * - 素材種別（写真/EPS/PDF）を MaterialKind と newsItemId slug に反映
 * - 出力は UTF-16BE 宣言付き XML 文字列
 *
 * @param {Object} params 写真系素材用の出稿パラメータ
 * @returns {string} NewsML2CTS XML
 */
export function buildPhotoNewsML2CTS(params?: NewsML2CTSParams): string {
  const {
    memoPriority = 'ベタ',
    memoRegistrationDate = '',
    memoCategory = '',
    reporterName = '未設定',
    eventStart,
    username = 'user',
    materialHeadline = '',
    caption = '',
    materialKind = '写真', // '画像' 'EPS' 'PDF' を含む
    scheduleId,
    materialSerial = 1,
    memoType = 'NEC',
    existingThemeDuid,
    existingNewsItemId,
    linkUrl = '',
    editInfo = '',
    entityFileName = '',
    imageWidth,
    imageHeight,
  } = params || {};

  const now = new Date();

  const startDate = eventStart ? new Date(eventStart) : now;
  const dateId = ymd(startDate);
  const dateAndTime = `${ymd(now)}T${hhmmss(now)}${TZ}`;
  const baseSeq = `${ymd(now)}${hhmmss(now)}${String(Math.floor(Math.random() * 90) + 10)}`;
  // themeDuid / newsItemId をスケジュールIDベースで生成（指定が無ければスケジュール日時ベース）
  const scheduleTimeStr = eventStart
    ? `${ymd(startDate)}${hhmmss(startDate)}`
    : (memoRegistrationDate
        ? `${String(memoRegistrationDate).replace(/-/g, '')}000000`
        : `${ymd(now)}${hhmmss(now)}`);
  const { newsItemId, themeDuid } = buildIds({
    scheduleId,
    materialKind,
    materialSerial,
    baseSeq,
    scheduleTimeStr,
    existingThemeDuid,
    existingNewsItemId,
  });

  const nyuukouYotei = buildNyuukouYotei({
    memoRegistrationDate,
    eventStart,
    fallbackDate: startDate,
    tz: TZ,
  });
  const urgencyFormalName = URGENCY_MAP[memoPriority] || '5';
  const necGenre = resolveDhNewsGenre(memoType);
  const resolvedEntityFileName = entityFileName || (() => {
    const fallbackExtMap: Record<string, string> = {
      写真: 'jpg',
      画像: 'jpg',
      ＥＰＳ: 'eps',
      EPS: 'eps',
      ＰＤＦ: 'pdf',
      PDF: 'pdf',
    };
    const fallbackExt = fallbackExtMap[materialKind] || 'jpg';
    return `${newsItemId}.${fallbackExt}`;
  })();
  const { format, mimeType } = resolveEntityFormat(resolvedEntityFileName);
  const characteristics = [
    '          <Characteristics>',
    '            <Property FormalName="ColorSpace" Value="4" Scheme="NecProperties"/>',
    '            <Property FormalName="IsColor" Value="0" Scheme="NecProperties"/>',
    imageWidth != null ? `            <Property FormalName="Width" Value="${escapeXml(String(imageWidth))}" Scheme="NecProperties"/>` : '',
    imageHeight != null ? `            <Property FormalName="Height" Value="${escapeXml(String(imageHeight))}" Scheme="NecProperties"/>` : '',
    '          </Characteristics>',
  ].filter(Boolean).join('\n');

  return `<?xml version="1.0" encoding="UTF-16BE"?>
<NewsML>
${params?.includeCatalog === false ? '' : '  <Catalog Href="../catalog/catalog.NecMasterCatalog.xml"/>\n'}  <NewsEnvelope>
    <DateAndTime>${escapeXml(dateAndTime)}</DateAndTime>
    <NewsProduct FormalName="NEC-NewsML:1.0" Scheme="NecNewsProduct"/>
    <Priority FormalName="${escapeXml(urgencyFormalName)}" Scheme="IptcPriority"/>
  </NewsEnvelope>
  <NewsItem>
    <Identification>
      <NewsIdentifier>
        <ProviderId>daily-tohoku.co.jp</ProviderId>
        <DateId>${escapeXml(dateId)}</DateId>
        <NewsItemId>${escapeXml(newsItemId)}</NewsItemId>
        <RevisionId PreviousRevision="1" Update="N">2</RevisionId>
        <PublicIdentifier>urn:newsml:daily-tohoku.co.jp:${escapeXml(dateId)}:${escapeXml(newsItemId)}:2</PublicIdentifier>
      </NewsIdentifier>
    </Identification>
    <NewsManagement>
      <NewsItemType FormalName="News"/>
      <FirstCreated>${escapeXml(dateAndTime)}</FirstCreated>
      <ThisRevisionCreated>${escapeXml(dateAndTime)}</ThisRevisionCreated>
      <Status FormalName="Usable" Scheme="IptcStatus"/>
      <Urgency FormalName="${escapeXml(urgencyFormalName)}" Scheme=""/>
    </NewsManagement>
    <NewsComponent Duid="NC001-root" xml:lang="ja-JP" EquivalentsList="no">
      <BasisForChoice>./Role/@FormalName</BasisForChoice>
      <NewsLines>
        <HeadLine>${escapeXml(materialHeadline || '')}</HeadLine>
        <ByLine>${escapeXml(reporterName)}</ByLine>
        <CreditLine>自社</CreditLine>
        <CopyrightLine>Copyright(C) ${escapeXml(String(now.getFullYear()))} (c)DAILYTOHOKU All rights reserved.</CopyrightLine>
        <NewsLine>
          <NewsLineType FormalName="EditInfo" Scheme="NskNewsLineType"/>
          <NewsLineText>${escapeXml(editInfo || '')}</NewsLineText>
        </NewsLine>
        <NewsLine>
          <NewsLineType FormalName="Caption" Scheme="NskNewsLineType"/>
          <NewsLineText>${escapeXml(caption || '')}</NewsLineText>
        </NewsLine>
      </NewsLines>
      <AdministrativeMetadata>
        <Provider>
          <Party FormalName="DAILYTOHOKU" Scheme="NskParty"/>
        </Provider>
        <Creator>
          <Party FormalName="DAILYTOHOKU" Scheme="NskParty"/>
        </Creator>
      </AdministrativeMetadata>
      <RightsMetadata>
        <Copyright>
          <CopyrightHolder>THE DAILY TOHOKU SHIMBUN</CopyrightHolder>
          <CopyrightDate>${escapeXml(String(now.getFullYear()))}</CopyrightDate>
        </Copyright>
      </RightsMetadata>
      <DescriptiveMetadata>
        <Language FormalName="ja" Scheme="ISO639"/>
        <SubjectCode>
          <Subject FormalName="${escapeXml(memoCategory || '')}"/>
        </SubjectCode>
      </DescriptiveMetadata>
      <Metadata>
        <MetadataType FormalName="NecProperties" Scheme="NecMetadataTypes"/>
        <Property FormalName="IsDesk" Value="false" Scheme="NecProperties"/>
        <Property FormalName="FromHotFolder" Value="on" Scheme="NecProperties"/>
        <Property FormalName="NewsGenre" Value="${escapeXml(necGenre)}" Scheme="NecProperties"/>
        <Property FormalName="MemoID" Value="${escapeXml(themeDuid)}" Scheme="NecProperties"/>
        <Property FormalName="ChildMemoID" Value="${escapeXml(newsItemId)}" Scheme="NecProperties"/>
        <Property FormalName="DispatchPlace" Value="本社" Scheme="NecProperties"/>
        <Property FormalName="DistributingAgency" Value="自社" Scheme="NecProperties"/>
        <Property FormalName="SecondUse" Value="true" Scheme="NecProperties"/>
        <Property FormalName="RePublishingFlag" Value="true" Scheme="NecProperties"/>
        <Property FormalName="HumanRights" Value="off" Scheme="NecProperties"/>
        <Property FormalName="LinkURL" Value="${escapeXml(linkUrl || '')}" Scheme="NecProperties"/>
        <Property FormalName="QuickReportNgFlag" Value="off" Scheme="NecProperties"/>
        <Property FormalName="CreatorID" Value="${escapeXml(username)}" Scheme="NecProperties"/>
        <Property FormalName="MaterialKind" Value="${escapeXml(materialKind)}" Scheme="NecProperties"/>
        <Property FormalName="NyuukouYotei" Value="${escapeXml(nyuukouYotei)}" Scheme="NecProperties"/>
      </Metadata>
      <Metadata>
        <MetadataType FormalName="KyodoMetadata" Scheme="KydMetadataTypes" />
        <Property FormalName="RelatedInfo" Scheme="KydProperty">
          <Property FormalName="RelatedTo" Scheme="KydProperty">
            <Property FormalName="RelatedID" Scheme="KydProperty" Value="${escapeXml(themeDuid)}" />
            <Property FormalName="RelatedKind" Scheme="KydProperty" Value="memo" />
          </Property>
          <Property FormalName="RelatedTo" Scheme="KydProperty">
            <Property FormalName="RelatedID" Scheme="KydProperty" Value="${escapeXml(newsItemId)}" />
            <Property FormalName="RelatedKind" Scheme="KydProperty" Value="child_memo" />
          </Property>
        </Property>
      </Metadata>
      <NewsComponent Duid="NC001-BaseImage" xml:lang="ja-JP">
        <Role FormalName="BaseImage" Scheme="IptcRole"/>
        <ContentItem Href="${escapeXml(resolvedEntityFileName)}">
          <MediaType FormalName="Photo" Scheme="IptcMediaTypes"/>
          <Format FormalName="${escapeXml(format)}" Scheme="IptcFormat"/>
          <MimeType FormalName="${escapeXml(mimeType)}" Scheme="IptcMimeTypes"/>
          ${characteristics}
        </ContentItem>
      </NewsComponent>
    </NewsComponent>
  </NewsItem>
</NewsML>`;
}

/**
 * 記事の実体（Version1）NewsML2CTS を生成する
 *
 * 主な仕様（NEC-NewsML仕様書・記事実体サンプル準拠）:
 * - NC001-Main コンポーネントの ContentItem/DataContent に記事本文（プレーンテキスト）を格納
 * - RevisionId はメモ(1)の改訂版として 2 を設定
 * - 出力は UTF-16BE 宣言付き XML 文字列
 *
 * @param {Object} params 実体出稿パラメータ
 * @param {string} params.articleBody DataContent に設定する記事本文
 * @param {string} params.memoId テーマのMemoID
 * @param {string} params.childMemoId 素材のChildMemoID（NewsItemIdにも使用）
 * @returns {string} NewsML2CTS XML
 */
export function buildArticleEntityNewsML2CTS(params?: NewsML2CTSParams): string {
  const {
    memoPriority = 'ベタ',
    reporterName = '未設定',
    username = 'user',
    materialHeadline = '',
    memoType = 'ＮＥＣ',
    memoId = '',
    childMemoId = '',
    linkUrl = '',
    editInfo = '',
    memoCategory = '',
    articleBody = '',
  } = params || {};

  const now = new Date();

  const dateId = ymd(now);
  const dateAndTime = `${ymd(now)}T${hhmmss(now)}${TZ}`;


  const urgencyFormalName = URGENCY_MAP[memoPriority] || '5';

  return `<?xml version="1.0" encoding="UTF-16BE"?>
<NewsML>
  <NewsEnvelope>
    <DateAndTime>${escapeXml(dateAndTime)}</DateAndTime>
    <NewsProduct FormalName="NEC-NewsML:1.0" Scheme="NecNewsProduct"/>
    <Priority FormalName="${escapeXml(urgencyFormalName)}" Scheme="IptcPriority"/>
  </NewsEnvelope>
  <NewsItem>
    <Identification>
      <NewsIdentifier>
        <ProviderId>daily-tohoku.co.jp</ProviderId>
        <DateId>${escapeXml(dateId)}</DateId>
        <NewsItemId>${escapeXml(childMemoId)}</NewsItemId>
        <RevisionId PreviousRevision="1" Update="N">2</RevisionId>
        <PublicIdentifier>urn:newsml:daily-tohoku.co.jp:${escapeXml(dateId)}:${escapeXml(childMemoId)}:2</PublicIdentifier>
      </NewsIdentifier>
    </Identification>
    <NewsManagement>
      <NewsItemType FormalName="News"/>
      <FirstCreated>${escapeXml(dateAndTime)}</FirstCreated>
      <ThisRevisionCreated>${escapeXml(dateAndTime)}</ThisRevisionCreated>
      <Status FormalName="Usable" Scheme="IptcStatus"/>
      <Urgency FormalName="${escapeXml(urgencyFormalName)}" Scheme=""/>
    </NewsManagement>
    <NewsComponent Duid="NC001-root" xml:lang="ja-JP" EquivalentsList="no">
      <BasisForChoice>./Role/@FormalName</BasisForChoice>
      <NewsLines>
        <HeadLine>${escapeXml(materialHeadline || '')}</HeadLine>
        <ByLine>${escapeXml(reporterName)}</ByLine>
        <CopyrightLine>Copyright(C) ${escapeXml(String(now.getFullYear()))} (c)DAILYTOHOKU All rights reserved.</CopyrightLine>
        <NewsLine>
          <NewsLineType FormalName="EditInfo" Scheme="NskNewsLineType"/>
          <NewsLineText>${escapeXml(editInfo || '')}</NewsLineText>
        </NewsLine>
      </NewsLines>
      <AdministrativeMetadata>
        <Provider>
          <Party FormalName="DAILYTOHOKU" Scheme="NskParty"/>
        </Provider>
        <Creator>
          <Party FormalName="DAILYTOHOKU" Scheme="NskParty"/>
        </Creator>
      </AdministrativeMetadata>
      <RightsMetadata>
        <Copyright>
          <CopyrightHolder>THE DAILY TOHOKU SHIMBUN</CopyrightHolder>
          <CopyrightDate>${escapeXml(String(now.getFullYear()))}</CopyrightDate>
        </Copyright>
      </RightsMetadata>
      <DescriptiveMetadata>
        <Language FormalName="ja" Scheme="ISO639"/>
        <SubjectCode>
          <Subject FormalName="${escapeXml(memoCategory || '')}"/>
        </SubjectCode>
      </DescriptiveMetadata>
      <Metadata>
        <MetadataType FormalName="NecProperties" Scheme="NecMetadataTypes"/>
        <Property FormalName="IsDesk" Value="false" Scheme="NecProperties"/>
        <Property FormalName="FromHotFolder" Value="on" Scheme="NecProperties"/>
        <Property FormalName="NewsGenre" Value="${escapeXml(memoType)}" Scheme="NecProperties"/>
        <Property FormalName="MemoID" Value="${escapeXml(memoId)}" Scheme="NecProperties"/>
        <Property FormalName="ChildMemoID" Value="${escapeXml(childMemoId)}" Scheme="NecProperties"/>
        <Property FormalName="SecondUse" Value="true" Scheme="NecProperties"/>
        <Property FormalName="RePublishingFlag" Value="true" Scheme="NecProperties"/>
        <Property FormalName="HumanRights" Value="off" Scheme="NecProperties"/>
        <Property FormalName="LinkURL" Value="${escapeXml(linkUrl || '')}" Scheme="NecProperties"/>
        <Property FormalName="QuickReportNgFlag" Value="off" Scheme="NecProperties"/>
        <Property FormalName="CreatorID" Value="${escapeXml(username)}" Scheme="NecProperties"/>
      </Metadata>
      <Metadata>
        <MetadataType FormalName="KyodoMetadata" Scheme="KydMetadataTypes"/>
        <Property FormalName="RelatedInfo" Scheme="KydProperty">
          <Property FormalName="RelatedTo" Scheme="KydProperty">
            <Property FormalName="RelatedID" Scheme="KydProperty" Value="${escapeXml(memoId)}"/>
            <Property FormalName="RelatedKind" Scheme="KydProperty" Value="memo"/>
          </Property>
          <Property FormalName="RelatedTo" Scheme="KydProperty">
            <Property FormalName="RelatedID" Scheme="KydProperty" Value="${escapeXml(childMemoId)}"/>
            <Property FormalName="RelatedKind" Scheme="KydProperty" Value="child_memo"/>
          </Property>
        </Property>
      </Metadata>
      <NewsComponent Duid="NC001-Main" xml:lang="ja-JP">
        <Role FormalName="Main" Scheme="IptcRole"/>
        <ContentItem>
          <MediaType FormalName="Text" Scheme="IptcMediaTypes"/>
          <MimeType FormalName="text/plain" Scheme="IptcMimeTypes"/>
          <Characteristics>
            <Property FormalName="FlowDirection" Value="0" Scheme="NecProperties"/>
            <Property FormalName="CharsPerLine" Value="12" Scheme="NecProperties"/>
            <Property FormalName="MidashiWaku" Value="0,0,0" Scheme="NecProperties"/>
          </Characteristics>
          <DataContent>${escapeXml(articleBody || '')}</DataContent>
        </ContentItem>
      </NewsComponent>
    </NewsComponent>
  </NewsItem>
</NewsML>`;
}

// 添付ファイルの拡張子→Format/MimeType（IptcFormat/IptcMimeTypes）変換
const resolveEntityFormat = (fileName?: string): { format: string; mimeType: string } => {
  const ext = (/\.([^.]+)$/.exec(fileName || '')?.[1] || '').toLowerCase();
  const map: Record<string, { format: string; mimeType: string }> = {
    jpg: { format: 'JPEG Baseline', mimeType: 'image/jpeg' },
    jpeg: { format: 'JPEG Baseline', mimeType: 'image/jpeg' },
    png: { format: 'PNG', mimeType: 'image/png' },
    gif: { format: 'GIF', mimeType: 'image/gif' },
    tif: { format: 'TIFF', mimeType: 'image/tiff' },
    tiff: { format: 'TIFF', mimeType: 'image/tiff' },
    eps: { format: 'EPS', mimeType: 'application/postscript' },
    pdf: { format: 'PDF', mimeType: 'application/pdf' },
  };
  return map[ext] || { format: ext.toUpperCase() || 'Unknown', mimeType: 'application/octet-stream' };
};

/**
 * 写真/EPS/PDF の実体（Version1）NewsML2CTS を生成する
 *
 * 主な仕様（NEC-NewsML仕様書・写真実体サンプル準拠）:
 * - NC001-BaseImage コンポーネントの ContentItem Href に実体ファイル名を設定
 * - 実体ファイルは本XMLと同一フォルダに配置される前提（Hrefはファイル名のみ）
 * - RevisionId はメモ(1)の改訂版として 2 を設定
 * - 出力は UTF-16BE 宣言付き XML 文字列
 *
 * @param {Object} params 実体出稿パラメータ
 * @param {string} params.entityFileName ContentItem Href に設定する実体ファイル名
 * @param {string} params.memoId テーマのMemoID
 * @param {string} params.childMemoId 素材のChildMemoID（NewsItemIdにも使用）
 * @returns {string} NewsML2CTS XML
 */
export function buildPhotoEntityNewsML2CTS(params?: NewsML2CTSParams): string {
  const {
    memoPriority = 'ベタ',
    reporterName = '未設定',
    username = 'user',
    materialHeadline = '',
    memoType = 'ＮＥＣ',
    memoId = '',
    childMemoId = '',
    linkUrl = '',
    editInfo = '',
    memoCategory = '',
    caption = '',
    entityFileName = '',
    imageWidth,
    imageHeight,
  } = params || {};

  const now = new Date();

  const dateId = ymd(now);
  const dateAndTime = `${ymd(now)}T${hhmmss(now)}${TZ}`;


  const urgencyFormalName = URGENCY_MAP[memoPriority] || '5';
  const { format, mimeType } = resolveEntityFormat(entityFileName);

  const characteristics = (imageWidth && imageHeight) ? `
          <Characteristics>
            <Property FormalName="Width" Value="${escapeXml(String(imageWidth))}" Scheme="NecProperties"/>
            <Property FormalName="Height" Value="${escapeXml(String(imageHeight))}" Scheme="NecProperties"/>
          </Characteristics>` : '';

  return `<?xml version="1.0" encoding="UTF-16BE"?>
<NewsML>
  <NewsEnvelope>
    <DateAndTime>${escapeXml(dateAndTime)}</DateAndTime>
    <NewsProduct FormalName="NEC-NewsML:1.0" Scheme="NecNewsProduct"/>
    <Priority FormalName="${escapeXml(urgencyFormalName)}" Scheme="IptcPriority"/>
  </NewsEnvelope>
  <NewsItem>
    <Identification>
      <NewsIdentifier>
        <ProviderId>daily-tohoku.co.jp</ProviderId>
        <DateId>${escapeXml(dateId)}</DateId>
        <NewsItemId>${escapeXml(childMemoId)}</NewsItemId>
        <RevisionId PreviousRevision="1" Update="N">2</RevisionId>
        <PublicIdentifier>urn:newsml:daily-tohoku.co.jp:${escapeXml(dateId)}:${escapeXml(childMemoId)}:2</PublicIdentifier>
      </NewsIdentifier>
    </Identification>
    <NewsManagement>
      <NewsItemType FormalName="News"/>
      <FirstCreated>${escapeXml(dateAndTime)}</FirstCreated>
      <ThisRevisionCreated>${escapeXml(dateAndTime)}</ThisRevisionCreated>
      <Status FormalName="Usable" Scheme="IptcStatus"/>
      <Urgency FormalName="${escapeXml(urgencyFormalName)}" Scheme=""/>
    </NewsManagement>
    <NewsComponent Duid="NC001-root" xml:lang="ja-JP" EquivalentsList="no">
      <BasisForChoice>./Role/@FormalName</BasisForChoice>
      <NewsLines>
        <HeadLine>${escapeXml(materialHeadline || '')}</HeadLine>
        <ByLine>${escapeXml(reporterName)}</ByLine>
        <CreditLine>自社</CreditLine>
        <CopyrightLine>Copyright(C) ${escapeXml(String(now.getFullYear()))} (c)DAILYTOHOKU All rights reserved.</CopyrightLine>
        <NewsLine>
          <NewsLineType FormalName="EditInfo" Scheme="NskNewsLineType"/>
          <NewsLineText>${escapeXml(editInfo || '')}</NewsLineText>
        </NewsLine>
        <NewsLine>
          <NewsLineType FormalName="Caption" Scheme="NskNewsLineType"/>
          <NewsLineText>${escapeXml(caption || '')}</NewsLineText>
        </NewsLine>
      </NewsLines>
      <AdministrativeMetadata>
        <Provider>
          <Party FormalName="DAILYTOHOKU" Scheme="NskParty"/>
        </Provider>
        <Creator>
          <Party FormalName="DAILYTOHOKU" Scheme="NskParty"/>
        </Creator>
      </AdministrativeMetadata>
      <RightsMetadata>
        <Copyright>
          <CopyrightHolder>THE DAILY TOHOKU SHIMBUN</CopyrightHolder>
          <CopyrightDate>${escapeXml(String(now.getFullYear()))}</CopyrightDate>
        </Copyright>
      </RightsMetadata>
      <DescriptiveMetadata>
        <Language FormalName="ja" Scheme="ISO639"/>
        <SubjectCode>
          <Subject FormalName="${escapeXml(memoCategory || '')}"/>
        </SubjectCode>
      </DescriptiveMetadata>
      <Metadata>
        <MetadataType FormalName="NecProperties" Scheme="NecMetadataTypes"/>
        <Property FormalName="IsDesk" Value="false" Scheme="NecProperties"/>
        <Property FormalName="FromHotFolder" Value="on" Scheme="NecProperties"/>
        <Property FormalName="NewsGenre" Value="${escapeXml(memoType)}" Scheme="NecProperties"/>
        <Property FormalName="MemoID" Value="${escapeXml(memoId)}" Scheme="NecProperties"/>
        <Property FormalName="ChildMemoID" Value="${escapeXml(childMemoId)}" Scheme="NecProperties"/>
        <Property FormalName="DispatchPlace" Value="本社" Scheme="NecProperties"/>
        <Property FormalName="DistributingAgency" Value="自社" Scheme="NecProperties"/>
        <Property FormalName="SecondUse" Value="true" Scheme="NecProperties"/>
        <Property FormalName="RePublishingFlag" Value="true" Scheme="NecProperties"/>
        <Property FormalName="HumanRights" Value="off" Scheme="NecProperties"/>
        <Property FormalName="LinkURL" Value="${escapeXml(linkUrl || '')}" Scheme="NecProperties"/>
        <Property FormalName="QuickReportNgFlag" Value="off" Scheme="NecProperties"/>
        <Property FormalName="CreatorID" Value="${escapeXml(username)}" Scheme="NecProperties"/>
      </Metadata>
      <Metadata>
        <MetadataType FormalName="KyodoMetadata" Scheme="KydMetadataTypes"/>
        <Property FormalName="RelatedInfo" Scheme="KydProperty">
          <Property FormalName="RelatedTo" Scheme="KydProperty">
            <Property FormalName="RelatedID" Scheme="KydProperty" Value="${escapeXml(memoId)}"/>
            <Property FormalName="RelatedKind" Scheme="KydProperty" Value="memo"/>
          </Property>
          <Property FormalName="RelatedTo" Scheme="KydProperty">
            <Property FormalName="RelatedID" Scheme="KydProperty" Value="${escapeXml(childMemoId)}"/>
            <Property FormalName="RelatedKind" Scheme="KydProperty" Value="child_memo"/>
          </Property>
        </Property>
      </Metadata>
      <NewsComponent Duid="NC001-BaseImage" xml:lang="ja-JP">
        <Role FormalName="BaseImage" Scheme="IptcRole"/>
        <ContentItem Href="${escapeXml(entityFileName)}">
          <MediaType FormalName="Photo" Scheme="IptcMediaTypes"/>
          <Format FormalName="${escapeXml(format)}" Scheme="IptcFormat"/>
          <MimeType FormalName="${escapeXml(mimeType)}" Scheme="IptcMimeTypes"/>${characteristics}
        </ContentItem>
      </NewsComponent>
    </NewsComponent>
  </NewsItem>
</NewsML>`;
}
